import { useEffect, useState } from 'react'
import type { RangeKey } from '@dataTypes/dashboard.types'
import type { DashboardMetricItemData } from '../../types/dashboardMetrics.types'
import {
  getPatientStatusOverview,
  getPatientStatusPatients,
  type PatientStatusAxisConfig,
  type PatientStatusChartPoint,
} from '@services/api/dashboard/patientStatus.api'
import {
  getPatientStatusAxisConfig,
  getPatientStatusData,
} from '@components/pages/Dashboard/Operations/operationsMockData'
import { isDashboardDevMocksEnabled } from '@utils/dashboardDevMocks.util'
import {
  getPatientStatusDetailMetrics,
  getPatientStatusDetailTableData,
  type PatientStatusDetailRow,
} from '@pages/Dashboard/PatientStatusDetail/patientStatusDetail.mockData'

export type PatientStatusOverviewState = {
  metrics: DashboardMetricItemData[]
  chartData: PatientStatusChartPoint[]
  axis: PatientStatusAxisConfig
  loading: boolean
  error: string | null
  /** Overview response has arrived (`overview != null`). */
  hasPayload: boolean
}

/**
 * Loads the Operations → Patient status overview (KPI metrics + chart + axis).
 * Falls back to mock data while loading or on error.
 */
export function usePatientStatusOverview(
  range: RangeKey,
  date?: string,
  enabled = true,
  queued = false,
): PatientStatusOverviewState {
  const [overview, setOverview] = useState<{
    metrics: DashboardMetricItemData[]
    chartData: PatientStatusChartPoint[]
    axis: PatientStatusAxisConfig
  } | null>(null)
  const waiting = queued && !enabled
  const [loading, setLoading] = useState(enabled || waiting)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled) {
      setLoading(waiting)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)

    getPatientStatusOverview(range, date)
      .then((result) => {
        if (!cancelled) setOverview(result)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(
          e instanceof Error ? e.message : 'Failed to load patient status',
        )
        setOverview(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [range, date, enabled, waiting])

  const useMocks = isDashboardDevMocksEnabled()
  const fallbackMetrics = getPatientStatusDetailMetrics(range)
  const fallbackChartData = getPatientStatusData(range)
  const fallbackAxis = getPatientStatusAxisConfig(range)

  return {
    metrics: overview?.metrics ?? (useMocks ? fallbackMetrics : []),
    chartData: overview?.chartData ?? (useMocks ? fallbackChartData : []),
    axis: overview?.axis ?? fallbackAxis,
    loading: loading || waiting,
    error,
    hasPayload: overview != null,
  }
}

export type PatientStatusPatientsState = {
  rows: PatientStatusDetailRow[]
  loading: boolean
  error: string | null
}

/** Loads the Patient status detail table rows with mock fallback. */
export function usePatientStatusPatients(
  range: RangeKey,
  date?: string,
): PatientStatusPatientsState {
  const [rows, setRows] = useState<PatientStatusDetailRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    getPatientStatusPatients(range, date)
      .then((result) => {
        if (!cancelled) setRows(result)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(
          e instanceof Error ? e.message : 'Failed to load patient status list',
        )
        setRows(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [range, date])

  const fallback = isDashboardDevMocksEnabled()
    ? getPatientStatusDetailTableData(range)
    : []

  return {
    rows: rows ?? fallback,
    loading,
    error,
  }
}
